import React, { useCallback, useEffect, useState } from 'react'
import { App, Button, Form, Input, Modal, Select, Space, Table, Tag } from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { sellerRolesAPI, sellerUsersAPI, type SellerUserPayload } from '../utils/sellerApi'
import { useSellerAuth } from '../contexts/SellerAuthContext'

interface Role {
  id: number
  display_name: string
  name: string
}

interface UserRole {
  id: number
  display_name: string
  name: string
}

interface SellerUser {
  id: number
  name: string
  email: string
  phone?: string | null
  status: string
  roles: UserRole[]
}

const UsersPage = () => {
  const [users, setUsers] = useState<SellerUser[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingUser, setEditingUser] = useState<SellerUser | null>(null)
  const [form] = Form.useForm<SellerUserPayload>()
  const { hasPermission } = useSellerAuth()
  const { message } = App.useApp()

  const canCreateUser = hasPermission('users.create')
  const canUpdateUser = hasPermission('users.update')
  const canDeleteUser = hasPermission('users.delete')

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    try {
      const response = await sellerUsersAPI.list()
      setUsers(response.data.data.data ?? [])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load team members')
    } finally {
      setLoading(false)
    }
  }, [message])

  const fetchRoles = useCallback(async () => {
    try {
      const response = await sellerRolesAPI.list()
      setRoles(response.data.data.data ?? [])
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to load roles')
    }
  }, [message])

  useEffect(() => {
    void Promise.all([fetchUsers(), fetchRoles()])
  }, [fetchRoles, fetchUsers])

  const openCreate = () => {
    if (!canCreateUser) {
      message.warning('You are not permitted to create team members.')
      return
    }

    setEditingUser(null)
    form.resetFields()
    form.setFieldsValue({ status: 'active', role_ids: [] })
    setModalVisible(true)
  }

  const openEdit = (user: SellerUser) => {
    if (!canUpdateUser) {
      message.warning('You are not permitted to update team members.')
      return
    }

    setEditingUser(user)
    form.setFieldsValue({
      name: user.name,
      phone: user.phone ?? undefined,
      status: user.status === 'inactive' ? 'inactive' : 'active',
      role_ids: user.roles.map((role) => role.id),
    })
    setModalVisible(true)
  }

  const closeModal = () => {
    setEditingUser(null)
    setModalVisible(false)
    form.resetFields()
  }

  const submit = async (values: SellerUserPayload) => {
    if (editingUser && !canUpdateUser) {
      message.warning('You are not permitted to update team members.')
      return
    }

    if (!editingUser && !canCreateUser) {
      message.warning('You are not permitted to create team members.')
      return
    }

    setSaving(true)
    try {
      if (editingUser) {
        await sellerUsersAPI.update(editingUser.id, values)
        message.success('Team member updated')
      } else {
        await sellerUsersAPI.create(values)
        message.success('Team member created. They can log in using staff OTP.')
      }
      closeModal()
      await fetchUsers()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save team member')
    } finally {
      setSaving(false)
    }
  }

  const deleteUser = async (id: number) => {
    if (!canDeleteUser) {
      message.warning('You are not permitted to delete team members.')
      return
    }

    try {
      await sellerUsersAPI.delete(id)
      message.success('Team member deleted')
      await fetchUsers()
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete team member')
    }
  }

  const columns: ColumnsType<SellerUser> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.name}</div>
          <div style={{ color: '#64748b', fontSize: 12 }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (value) => value || 'N/A',
    },
    {
      title: 'Roles',
      key: 'roles',
      render: (_, record) => (
        <Space wrap>
          {(record.roles ?? []).map((role) => (
            <Tag key={role.id}>{role.display_name}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => <Tag color={record.status === 'active' ? 'green' : 'default'}>{record.status}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          {canUpdateUser ? (
            <Button icon={<EditOutlined />} onClick={() => openEdit(record)}>
              Edit
            </Button>
          ) : null}
          {canDeleteUser ? (
            <Button danger icon={<DeleteOutlined />} onClick={() => void deleteUser(record.id)}>
              Delete
            </Button>
          ) : null}
          {!canUpdateUser && !canDeleteUser ? <Tag color="default">Read only</Tag> : null}
        </Space>
      ),
    },
  ]

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28 }}>Seller Team</h1>
          <div style={{ color: '#64748b', marginTop: 6 }}>Manage employee accounts, assign roles, and use OTP-based staff sign-in without passwords.</div>
        </div>
        {canCreateUser ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add Team Member
          </Button>
        ) : null}
      </div>

      <Table columns={columns} dataSource={users} rowKey="id" loading={loading} pagination={{ pageSize: 10 }} scroll={{ x: 860 }} />

      <Modal title={editingUser ? 'Edit Team Member' : 'Add Team Member'} open={modalVisible} onCancel={closeModal} footer={null}>
        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="Full name" />
          </Form.Item>
          {!editingUser ? (
            <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Enter a valid email' }]}>
              <Input placeholder="employee@example.com" />
            </Form.Item>
          ) : null}
          <Form.Item name="phone" label="Phone">
            <Input placeholder="Optional phone number" />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true, message: 'Status is required' }]}>
            <Select options={[{ label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]} />
          </Form.Item>
          <Form.Item name="role_ids" label="Roles" rules={[{ required: true, message: 'Pick at least one role' }]}>
            <Select mode="multiple" options={roles.map((role) => ({ label: role.display_name, value: role.id }))} placeholder="Assign roles" />
          </Form.Item>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <Button onClick={closeModal}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              {editingUser ? 'Update User' : 'Create User'}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  )
}

export default UsersPage
